/**
 * Edit batching algorithm for the Simple Writing Replay UI.
 *
 * Transforms raw edit events into meaningful "key moments" that tell
 * the story of how a document was written. Instead of showing 247
 * individual edits, we batch them into ~30 moments like:
 * - "Typed 45 words over 3 minutes"
 * - "Pasted 147 words instantly"
 * - "Session resumed after 2 hours"
 *
 * Batching Rules (Priority Order):
 * 1. Session break: Gap >= 30 min -> 'Session resumed'
 * 2. Paste: Any paste -> Own moment 'Pasted X words'
 * 3. Undo/Redo: -> Own moment 'Undid X edits'
 * 4. Large edit: Single edit >= 20 chars -> Own moment
 * 5. Position jump: Cursor moved > 100 chars -> Break batch
 * 6. Time gap: > 2 seconds between edits -> Break batch
 * 7. Max duration: Batch > 5 minutes -> Break batch
 * 8. Typo correction: Delete + Insert within 1s at same position -> 'Fixed typo'
 * 9. Otherwise: Batch consecutive small edits -> 'Typed X words'
 */

import type { ExtensionEditEvent } from "@markeroo/shared";
import type { KeyMoment, KeyMomentType } from "../types";

/**
 * Defensive limits for extreme input sizes.
 */

/** Maximum number of events to process for batching (1 million) */
const MAX_BATCH_EVENT_COUNT = 1_000_000;

// Batching thresholds
const SESSION_BREAK_MS = 30 * 60 * 1000; // 30 minutes
const TIME_GAP_MS = 2 * 1000; // 2 seconds
const MAX_BATCH_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const LARGE_EDIT_CHARS = 20; // Characters
const POSITION_JUMP_CHARS = 100; // Characters
const TYPO_CORRECTION_MS = 1000; // 1 second

/**
 * Count words in a string.
 */
function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter((w) => w.length > 0).length;
}

/**
 * Format a duration in milliseconds to a human-readable string.
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return "Instant";
  if (ms < 60 * 1000) {
    const seconds = Math.round(ms / 1000);
    return `${seconds} sec`;
  }
  const minutes = Math.round(ms / (60 * 1000));
  return `${minutes} min`;
}

/**
 * Generate a unique ID for a moment.
 */
function generateMomentId(index: number, timestamp: string): string {
  return `moment-${index}-${timestamp}`;
}

/**
 * Calculate net character change for a set of events.
 */
function calculateNetChange(events: ExtensionEditEvent[]): number {
  let netChange = 0;
  for (const event of events) {
    switch (event.type) {
      case "insert":
      case "paste":
        netChange += event.content?.length ?? 0;
        break;
      case "delete":
        netChange -= event.length ?? 0;
        break;
      case "replace":
        netChange += (event.content?.length ?? 0) - (event.length ?? 0);
        break;
    }
  }
  return netChange;
}

/**
 * Calculate net word count change for a set of events.
 * This is an approximation based on inserted/deleted content.
 */
function calculateWordCount(events: ExtensionEditEvent[]): number {
  let wordCount = 0;
  for (const event of events) {
    switch (event.type) {
      case "insert":
      case "paste":
        wordCount += countWords(event.content ?? "");
        break;
      case "delete":
        wordCount -= countWords(event.deletedContent ?? "");
        break;
      case "replace":
        wordCount +=
          countWords(event.content ?? "") -
          countWords(event.deletedContent ?? "");
        break;
    }
  }
  return wordCount;
}

/**
 * Generate a human-readable summary for a moment.
 */
function generateSummary(
  type: KeyMomentType,
  wordCount: number,
  eventCount: number
): string {
  const absWords = Math.abs(wordCount);
  const wordLabel = absWords === 1 ? "word" : "words";

  switch (type) {
    case "session_start":
      return "Session resumed";
    case "paste":
      return `Pasted ${absWords} ${wordLabel}`;
    case "undo":
      return `Undid ${eventCount} ${eventCount === 1 ? "edit" : "edits"}`;
    case "deleted":
      return `Deleted ${absWords} ${wordLabel}`;
    case "typed":
    default:
      if (wordCount === 0) {
        return "Made small edits";
      }
      return `Typed ${absWords} ${wordLabel}`;
  }
}

/**
 * Check if an event is a typo correction pattern.
 * A typo correction is a delete followed by an insert at the same position
 * within 1 second.
 */
function isTypoCorrection(
  prevEvent: ExtensionEditEvent,
  currEvent: ExtensionEditEvent
): boolean {
  if (prevEvent.type !== "delete") return false;
  if (currEvent.type !== "insert") return false;

  const timeDiff =
    new Date(currEvent.timestamp).getTime() -
    new Date(prevEvent.timestamp).getTime();
  if (timeDiff > TYPO_CORRECTION_MS) return false;

  // Position should be at or near the deletion point
  const deleteEnd = prevEvent.position ?? 0;
  const insertPos = currEvent.position ?? 0;
  return Math.abs(deleteEnd - insertPos) <= 5;
}

/**
 * Determine if we should break the current batch based on the next event.
 */
function shouldBreakBatch(
  batch: ExtensionEditEvent[],
  nextEvent: ExtensionEditEvent,
  batchStartTime: Date
): { shouldBreak: boolean; reason?: "session_break" | "time_gap" | "position_jump" | "max_duration" } {
  if (batch.length === 0) {
    return { shouldBreak: false };
  }

  const lastEvent = batch[batch.length - 1];
  const lastTime = new Date(lastEvent.timestamp).getTime();
  const nextTime = new Date(nextEvent.timestamp).getTime();
  const timeDiff = nextTime - lastTime;

  // Rule 1: Session break (>= 30 min)
  if (timeDiff >= SESSION_BREAK_MS) {
    return { shouldBreak: true, reason: "session_break" };
  }

  // Rule 7: Max duration (> 5 minutes)
  const batchDuration = nextTime - batchStartTime.getTime();
  if (batchDuration > MAX_BATCH_DURATION_MS) {
    return { shouldBreak: true, reason: "max_duration" };
  }

  // Rule 6: Time gap (> 2 seconds)
  if (timeDiff > TIME_GAP_MS) {
    return { shouldBreak: true, reason: "time_gap" };
  }

  // Rule 5: Position jump (> 100 chars)
  const lastPosition = (lastEvent.position ?? 0) + (lastEvent.content?.length ?? 0);
  const positionDiff = Math.abs((nextEvent.position ?? 0) - lastPosition);
  if (positionDiff > POSITION_JUMP_CHARS) {
    return { shouldBreak: true, reason: "position_jump" };
  }

  return { shouldBreak: false };
}

/**
 * Create a KeyMoment from a batch of events.
 */
function createMoment(
  events: ExtensionEditEvent[],
  index: number,
  type: KeyMomentType,
  overrideSummary?: string
): KeyMoment {
  const startTime = new Date(events[0].timestamp);
  const endTime = new Date(events[events.length - 1].timestamp);
  const duration = endTime.getTime() - startTime.getTime();

  const netChange = calculateNetChange(events);
  const wordCount = calculateWordCount(events);

  const summary =
    overrideSummary ?? generateSummary(type, wordCount, events.length);

  return {
    id: generateMomentId(index, events[0].timestamp),
    type,
    summary,
    duration: formatDuration(duration),
    timestamp: events[0].timestamp,
    events,
    netChange,
    wordCount,
  };
}

/**
 * Batch raw edit events into meaningful key moments.
 *
 * This function implements the batching algorithm that transforms
 * hundreds of individual edit events into a manageable set of
 * "key moments" that tell the story of how a document was written.
 *
 * @param events - Raw edit events from the API (should be sorted by timestamp)
 * @returns Array of KeyMoment objects representing meaningful activities
 *
 * @example
 * ```typescript
 * const events = await api.getExtensionDocumentEvents(documentId);
 * const moments = batchEdits(events.events);
 *
 * // moments might contain:
 * // - { type: 'typed', summary: 'Typed 45 words', duration: '3 min', ... }
 * // - { type: 'paste', summary: 'Pasted 147 words', duration: 'Instant', ... }
 * // - { type: 'session_start', summary: 'Session resumed', ... }
 * ```
 */
export function batchEdits(events: ExtensionEditEvent[]): KeyMoment[] {
  // Handle edge case: empty events
  if (events.length === 0) {
    return [];
  }

  // Defensive limit: warn and truncate if too many events
  let eventsToProcess = events;
  if (events.length > MAX_BATCH_EVENT_COUNT) {
    console.warn(
      `[replay] batchEdits: Event count (${events.length}) exceeds limit (${MAX_BATCH_EVENT_COUNT}), truncating to most recent events`
    );
    // Take the most recent events (from the end)
    eventsToProcess = events.slice(-MAX_BATCH_EVENT_COUNT);
  }

  // Sort events by timestamp
  const sortedEvents = [...eventsToProcess].sort(
    (a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const moments: KeyMoment[] = [];
  let currentBatch: ExtensionEditEvent[] = [];
  let batchStartTime: Date | null = null;
  let momentIndex = 0;
  let nextMomentIsSessionBreak = false;

  /**
   * Finalize the current batch as a moment.
   */
  function finalizeBatch(isSessionBreak: boolean = false): void {
    if (currentBatch.length === 0) return;

    // Determine the type based on the events in the batch
    let type: KeyMomentType = "typed";

    // Check for typo correction pattern (delete followed by insert)
    if (currentBatch.length === 2) {
      if (isTypoCorrection(currentBatch[0], currentBatch[1])) {
        const summary = isSessionBreak ? "Session resumed" : "Fixed typo";
        moments.push(createMoment(currentBatch, momentIndex++, "typed", summary));
        currentBatch = [];
        batchStartTime = null;
        return;
      }
    }

    // Check if all events are deletions
    const allDeletes = currentBatch.every((e) => e.type === "delete");
    if (allDeletes) {
      type = "deleted";
    }

    // Check if net change is negative (more deletion than insertion)
    const netChange = calculateNetChange(currentBatch);
    if (netChange < 0 && !allDeletes) {
      type = "deleted";
    }

    const summary = isSessionBreak ? "Session resumed" : undefined;
    moments.push(createMoment(currentBatch, momentIndex++, type, summary));
    currentBatch = [];
    batchStartTime = null;
  }

  // Track the timestamp of the last processed event for session break detection
  let lastEventTime: Date | null = null;

  for (let i = 0; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];
    const eventTime = new Date(event.timestamp);

    // Check for session break against the last event (even if batch is empty)
    if (lastEventTime !== null) {
      const timeSinceLastEvent = eventTime.getTime() - lastEventTime.getTime();
      if (timeSinceLastEvent >= SESSION_BREAK_MS) {
        finalizeBatch(false);
        nextMomentIsSessionBreak = true;
      }
    }

    // Check if we need to break the batch for other reasons
    const breakResult = shouldBreakBatch(
      currentBatch,
      event,
      batchStartTime ?? eventTime
    );

    if (breakResult.shouldBreak) {
      finalizeBatch(false);

      // Mark that the next moment should be a session break
      // (but don't override if already set by session break check above)
      if (breakResult.reason === "session_break") {
        nextMomentIsSessionBreak = true;
      }
    }

    // Rule 2: Paste events get their own moment
    if (event.type === "paste") {
      finalizeBatch(false);
      const summary = nextMomentIsSessionBreak ? "Session resumed" : undefined;
      moments.push(createMoment([event], momentIndex++, "paste", summary));
      nextMomentIsSessionBreak = false;
      lastEventTime = eventTime;
      continue;
    }

    // Rule 3: Undo/Redo events get their own moment
    if (event.type === "undo" || event.type === "redo") {
      finalizeBatch(false);
      const summary = nextMomentIsSessionBreak ? "Session resumed" : undefined;
      moments.push(createMoment([event], momentIndex++, "undo", summary));
      nextMomentIsSessionBreak = false;
      lastEventTime = eventTime;
      continue;
    }

    // Rule 4: Large edits (>= 20 chars) get their own moment
    const eventSize =
      event.type === "insert" || event.type === "replace"
        ? event.content?.length ?? 0
        : event.type === "delete"
          ? event.length ?? 0
          : 0;

    if (eventSize >= LARGE_EDIT_CHARS) {
      finalizeBatch(false);

      const type = event.type === "delete" ? "deleted" : "typed";
      const summary = nextMomentIsSessionBreak ? "Session resumed" : undefined;
      moments.push(createMoment([event], momentIndex++, type, summary));
      nextMomentIsSessionBreak = false;
      lastEventTime = eventTime;
      continue;
    }

    // Add event to current batch
    if (currentBatch.length === 0) {
      batchStartTime = eventTime;
    }

    currentBatch.push(event);

    // Update last event time for session break detection
    lastEventTime = eventTime;
  }

  // Finalize any remaining batch
  finalizeBatch(nextMomentIsSessionBreak);

  return moments;
}
